import React, { useContext, useEffect, useMemo, useState } from 'react';

import { rows, songs } from '@/assets/icons';
import { LayoutContext } from '@/components/Layout';
import convertSetlitToOpenSetlistConfig from '@/components/Layout/components/GenerateSetlist/helpers/convertSetlitToOpenSetlistConfig';
import { NewMessage } from '@/components/NewMessage';
import { SetlistSummaryCard } from '@/components/SetlistSummaryCard';
import { RoundedButton } from '@/components/shared/RoundedButton';
import { SetlistPermissionSheet } from '@/components/shared/SetlistPermissionSheet';
import { DAYS_OF_WEEK, TeamMilestones } from '@/config/constants';
import { Permissions, useListMySetlistFeedbackRequestsQuery } from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { useAuth } from '@/providers/AuthProvider';
import { useLayout } from '@/providers/LayoutProvider';
import { usePermissions } from '@/providers/PermissionsProvider';
import { useServices } from '@/providers/ServicesProvider';
import { useSetlists } from '@/providers/SetlistsProvider';
import { useTeam } from '@/providers/TeamProvider/TeamProvider';
import { endOfWeek, startOfWeek } from 'date-fns';

import dashboardStyles from '../../Dashboard.module.scss';
import styles from './DashboardSetlists.module.scss';
import { EmptySetlistCard } from './EmptySetlistCard';

export const DashboardSetlists: React.FC<{
	onViewAllClick: () => void;
	onGenerateClick: () => void;
	hasViewAll?: boolean;
}> = ({ onViewAllClick, onGenerateClick, hasViewAll = false }) => {
	const { openAddSongs, openSetlistGenerate, openSetlistWithCallback } =
		useLayout();
	const { user } = useAuth();
	const { hasMilestones } = useTeam();
	const { services } = useServices();
	const { setlists, isLoading: setlistQueryLoading } = useSetlists();
	const { data: feedbackSetlists, isLoading: feedbackSetlistsLoading } =
		useListMySetlistFeedbackRequestsQuery();
	const { hasPermission } = usePermissions();
	const [initialSetlistLength, setinitialSetlistLength] = useState(0);
	const [permissionSheetOpen, setPermissionSheetOpen] = useState(false);
	const [newMessageOpen, setNewMessageOpen] = useState(false);

	const weeklySetlists = useMemo(() => {
		const now = new Date();
		const mondayThisWeek = startOfWeek(now, {
			weekStartsOn: 1,
		});
		const sundayThisWeek = endOfWeek(now, {
			weekStartsOn: 1,
		});

		return setlists.filter((s) => {
			const startAt = new Date(s.startAt);
			const isInFeedbackRequest =
				!feedbackSetlists?.listMySetlistFeedbackRequests.find(
					(f) => f.id === s.id,
				);
			const hasUserMadeSubmission = !s.submissions?.find(
				(sub) => sub.userId === user?.id,
			);
			const hasFeedback = !!s.feedback;
			return (
				mondayThisWeek < startAt &&
				startAt < sundayThisWeek &&
				!s.draft &&
				(isInFeedbackRequest
					? hasFeedback || hasUserMadeSubmission
					: true)
			);
		});
	}, [setlists, feedbackSetlists?.listMySetlistFeedbackRequests, user?.id]);

	const displaySetlists = useMemo(() => {
		if (weeklySetlists.length > 0) return weeklySetlists;
		const now = new Date();
		const futureSetlists = setlists.filter(
			(s) => !s.draft && new Date(s.startAt) > now,
		);
		if (futureSetlists.length === 0) return [];
		const nextSetlist = futureSetlists.reduce((soonest, current) =>
			new Date(current.startAt) < new Date(soonest.startAt)
				? current
				: soonest,
		);
		return [nextSetlist];
	}, [weeklySetlists, setlists]);

	const sectionTitle = useMemo(() => {
		if (weeklySetlists.length > 0) {
			// Derive from what's actually on the dashboard this week
			const allOnSunday = weeklySetlists.every(
				(s) => new Date(s.startAt).getDay() === 0,
			);
			if (!allOnSunday) return "This week's setlists";
			return weeklySetlists.length === 1
				? "This Sunday's setlist"
				: "This Sunday's setlists";
		}
		if (displaySetlists.length > 0) {
			// No current-week setlists, but a next-week fallback is shown
			return new Date(displaySetlists[0].startAt).getDay() === 0
				? "Next Sunday's setlist"
				: "Next week's setlist";
		}
		// Empty state: fall back to services config
		// Sunday is day 0 (date-fns) or day 7 (legacy server convention)
		const isSundayService = (day: number) =>
			DAYS_OF_WEEK[day] === 'Sunday' || day === 7;
		const sundayServices = services.filter((s) => isSundayService(s.day));
		const allSunday =
			services.length > 0 && sundayServices.length === services.length;
		if (!allSunday) return "This week's setlists";
		return sundayServices.length === 1
			? "This Sunday's setlist"
			: "This Sunday's setlists";
	}, [services, weeklySetlists, displaySetlists]);

	useEffect(() => {
		if (!initialSetlistLength) {
			setinitialSetlistLength(weeklySetlists.length);
		}
	}, [weeklySetlists.length, initialSetlistLength]);

	if (setlistQueryLoading || feedbackSetlistsLoading) {
		return null;
	}

	const hasSongsMilestone = hasMilestones([
		TeamMilestones.ADDED_INITIAL_SONGS,
	]);

	return (
		<>
			<div
				className={styles['root']}
				key={`dashboard-setlists-with-${displaySetlists.length}-setlists`}
			>
				<div className={styles['title-row']}>
					<div className={dashboardStyles['title']}>
						{sectionTitle}
					</div>
				</div>
				<div className={styles['setlists-row']}>
					{displaySetlists.length ? (
						displaySetlists.map((setlist, si) => {
							return (
								<SetlistSummaryCard
									key={setlist.id}
									size="md"
									setlistId={setlist.id}
									setlist={setlist}
									timeClassName={
										styles['setlist-summary-time']
									}
									className={classNames([
										si === displaySetlists.length - 1 &&
											styles['last'],
										styles['setlist-summary-card'],
									])}
									onClick={() => {
										openSetlistWithCallback(() => {},
										convertSetlitToOpenSetlistConfig(setlist));
									}}
								/>
							);
						})
					) : hasSongsMilestone ? (
						<EmptySetlistCard
							iconComponent={rows}
							iconStyle={{ fill: 'none' }}
							title="No setlists"
							subtitle="Create a setlist now"
							buttonLabel="Create setlist"
							onButtonClick={() => {
								if (!hasPermission(Permissions.TeamUpdate)) {
									setPermissionSheetOpen(true);
									return;
								}
								openSetlistGenerate();
							}}
						/>
					) : (
						<EmptySetlistCard
							iconComponent={songs}
							iconStyle={{ marginLeft: -3 }}
							title="No songs available"
							subtitle="Add songs to create a setlist."
							buttonLabel="Add songs"
							onButtonClick={openAddSongs}
						/>
					)}
				</div>
			</div>
			<SetlistPermissionSheet
				open={permissionSheetOpen}
				onClose={() => setPermissionSheetOpen(false)}
				onNewMessage={() => {
					setPermissionSheetOpen(false);
					setNewMessageOpen(true);
				}}
			/>
			<NewMessage
				open={newMessageOpen}
				handleClose={() => setNewMessageOpen(false)}
				initialMessage
			/>
		</>
	);
};
